import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import wizardService from '../services/wizardService';

const wizardSteps = [
  {
    id: "why",
    title: "Why Quantifaid Exists",
    subtitle: "AI Trust is the new currency markets",
    body: "Regulators, companies, and capital markets now expect proof that a company's AI is responsible and effective. Quantifaid is the first platform that measures both."
  },
  {
    id: "score",
    title: "What the AI Trust Score Means",
    body: [
      {
        grade: "AA / A",
        band: "85-100",
        signal: "Best-practice governance, low security risk, strong AI adoption."
      },
      {
        grade: "BBB / BB",
        band: "70-84",
        signal: "Solid foundation; a few gaps or slow adoption."
      },
      {
        grade: "B",
        band: "< 70",
        signal: "Material compliance or enablement issues to address."
      }
    ],
    footer: "Think of it as ESG for AI—but with real-time security and innovation data baked in."
  },
  {
    id: "investor",
    title: "If You're an Investor",
    body: [
      {
        heading: "Dive into the AI Trust Index",
        description: "Screen portfolio or investment targets same way you integrate ESG."
      },
      {
        heading: "See the story behind every score",
        description: "Governance evidence, live vulnerability stats, and AI-enablement signals are one click away—no black box."
      },
      {
        heading: "Stay ahead of regulation",
        description: "Index flags companies at risk from EU AI Act, GDPR, or emerging U.S. rules—before the market reacts."
      }
    ]
  },
  {
    id: "company",
    title: "If You're a Company",
    body: [
      {
        heading: "Run your first AI Trust Assessment (≈ 10 min)",
        description: "Upload existing policies, security scans, or SBOMs—no long questionnaires."
      },
      {
        heading: "Get an action-ready roadmap",
        description: "Quantifaid turns gaps into clear remediation tasks and tracks your progress."
      },
      {
        heading: "Showcase AI Enablement",
        description: "We score not only risk controls but also how effectively your teams use AI (code assistants, data platforms, etc.). High enablement lifts your grade and your valuation."
      }
    ]
  },
  {
    id: "methodology",
    title: "How We Rate—In One Slide",
    body: [
      {
        heading: "Evidence Upload",
        description: "Your documents or API connections."
      },
      {
        heading: "Live Risk Feeds",
        description: "NVD, OWASP, AI-incident DB, GDPR/EU AI Act text."
      },
      {
        heading: "Independent Review",
        description: "Methodology aligned to NIST AI RMF and audited quarterly."
      }
    ],
    footer: "All data encrypted in transit & at rest (ISO 27001 hosting)."
  },
  {
    id: "next",
    title: "Next Actions",
    actions: [
      { 
        label: "Explore AI Trust Index →", 
        value: "index",
        description: "Industry-wide leaderboard & filters.",
        bestFor: "Investors, analysts"
      },
      { 
        label: "Start My Assessment →", 
        value: "assessment",
        description: "Evidence-upload wizard.",
        bestFor: "Company teams"
      },
      { 
        label: "Skip for Now", 
        value: "skip", 
        variant: "secondary",
        description: "Straight to dashboard (you can start any time).",
        bestFor: "Anyone"
      }
    ]
  }
];

const Wizard = ({ isOpen, onClose }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const navigate = useNavigate();

  const currentStep = wizardSteps[currentStepIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const handleNext = () => {
    if (currentStepIndex < wizardSteps.length - 1) {
      setDirection(1);
      setCurrentStepIndex(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStepIndex > 0) {
      setDirection(-1);
      setCurrentStepIndex(prev => prev - 1);
    }
  };

  const handleAction = async (value) => {
    try {
      console.log('Completing wizard...');
      await wizardService.completeWizard();
      console.log('Wizard completed successfully');
      onClose();
      
      switch (value) {
        case 'index':
          navigate('/dashboard');
          break;
        case 'assessment':
          // You might want to trigger the assessment wizard here
          break;
        case 'skip':
          navigate('/dashboard');
          break;
      }
    } catch (error) {
      console.error('Error completing wizard:', error);
      navigate('/dashboard'); // Fallback to dashboard on error
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-background rounded-xl shadow-xl w-full max-w-6xl m-4 relative overflow-hidden"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-primary/10 transition-colors"
        >
          <X size={24} />
        </button>

        <div className="p-12">
          <div className="mb-8">
            <div className="flex justify-center space-x-2">
              {wizardSteps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-16 rounded-full ${
                    index === currentStepIndex ? 'bg-primary' : 'bg-primary/20'
                  }`}
                />
              ))}
            </div>
            <div className="text-center mt-4 text-text-secondary">
              Step {currentStepIndex + 1} of {wizardSteps.length}
            </div>
          </div>

          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentStepIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
            >
              <div className="flex flex-col items-center justify-center p-4 max-w-4xl mx-auto">
                <h2 className="text-3xl font-bold mb-4 text-text">{currentStep.title}</h2>
                {currentStep.subtitle && (
                  <p className="text-xl mb-6 text-primary italic">{currentStep.subtitle}</p>
                )}
                
                <div className="w-full mb-8">
                  {currentStep.id === 'score' ? (
                    <div className="space-y-6">
                      <div className="grid grid-cols-3 gap-4 text-sm font-medium text-text-secondary mb-4">
                        <div>Letter grade</div>
                        <div>Numeric band</div>
                        <div>Plain-English signal</div>
                      </div>
                      {currentStep.body.map((row, index) => (
                        <div key={index} className="grid grid-cols-3 gap-4 text-text">
                          <div className="font-bold">{row.grade}</div>
                          <div>{row.band}</div>
                          <div>{row.signal}</div>
                        </div>
                      ))}
                      <p className="text-text-secondary mt-6 italic">{currentStep.footer}</p>
                    </div>
                  ) : Array.isArray(currentStep.body) ? (
                    <div className="space-y-8">
                      {currentStep.body.map((item, index) => (
                        <div key={index} className="text-left">
                          {item.heading && (
                            <h3 className="text-lg font-semibold mb-2 text-text">{item.heading}</h3>
                          )}
                          <p className="text-text-secondary">{item.description}</p>
                        </div>
                      ))}
                      {currentStep.footer && (
                        <p className="text-text-secondary mt-6 italic">{currentStep.footer}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-text-secondary text-lg">{currentStep.body}</p>
                  )}
                </div>
                
                {currentStep.actions ? (
                  <div className="w-full max-w-2xl space-y-6">
                    {currentStep.actions.map((action) => (
                      <div key={action.value} className="flex flex-col space-y-2">
                        <button
                          onClick={() => handleAction(action.value)}
                          className={`w-full px-6 py-4 rounded-lg transition-colors text-lg ${
                            action.variant === 'secondary'
                              ? 'bg-primary/10 hover:bg-primary/20 text-text'
                              : 'bg-primary hover:bg-primary/90 text-white'
                          }`}
                        >
                          {action.label}
                        </button>
                        <div className="flex justify-between text-sm text-text-secondary px-2">
                          <span>{action.description}</span>
                          <span>Best for: {action.bestFor}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between w-full max-w-md mt-8">
                    <button
                      onClick={handlePrev}
                      disabled={currentStepIndex === 0}
                      className="px-6 py-3 rounded-lg bg-primary/10 hover:bg-primary/20 disabled:opacity-50 text-text"
                    >
                      Previous
                    </button>
                    <button
                      onClick={handleNext}
                      className="px-6 py-3 rounded-lg bg-primary hover:bg-primary/90 text-white"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default Wizard; 